/**
 * The Estimated value tab's window: how many days, the exact bounds, and
 * whether the page is drawing hours or days. Owned here so the tab reads as
 * layout, and so the refresh rule (re-read the same bounds, or move the
 * window forward when the clock has crossed a boundary) has a name.
 *
 * The choice persists through the usage page preferences, so the page opens
 * where the visitor left it (ported from upstream's usagePagePreferences,
 * pin e3e7cc3f; forge's metric axis is cost/tokens).
 *
 * @module usage/useUsageWindowSelection
 */
import { useState } from "react";

import { makeWindow } from "@cmmd-forge/shared/usageFormat";

import {
  readUsagePagePreferences,
  saveUsagePagePreferences,
} from "../components/usage/usagePagePreferences";

type UsageWindow = ReturnType<typeof makeWindow>;

const WINDOW_DAY_OPTIONS = [1, 7, 30, 90] as const;
type WindowDays = (typeof WINDOW_DAY_OPTIONS)[number];

function isWindowDays(days: number): days is WindowDays {
  return (WINDOW_DAY_OPTIONS as readonly number[]).includes(days);
}

function sameBounds(left: UsageWindow, right: UsageWindow): boolean {
  return (
    left.sinceDay === right.sinceDay &&
    left.untilDay === right.untilDay &&
    left.sinceTime === right.sinceTime &&
    left.untilTime === right.untilTime
  );
}

export function useUsageWindowSelection() {
  // `days` is a number on purpose: the schema's literal union would flow
  // into the state type and then refuse every off-schema render-time value.
  const [selection, setSelection] = useState<{ days: number; window: UsageWindow }>(() => {
    const days: number = readUsagePagePreferences().windowDays;
    // The initial bounds must match the resolution the tab reports: one day
    // is the rolling 24-hour window at "hour" resolution, not a daily bucket.
    return { days, window: makeWindow(days, undefined, days === 1 ? "hour" : "day") };
  });
  const isPast24Hours = selection.days === 1;
  const resolution = isPast24Hours ? "hour" : "day";

  const selectWindow = (days: number) => {
    // Persist only values the schema carries; an off-list day still renders,
    // it just is not remembered.
    if (isWindowDays(days)) {
      saveUsagePagePreferences({ ...readUsagePagePreferences(), windowDays: days });
    }
    setSelection({ days, window: makeWindow(days, undefined, days === 1 ? "hour" : "day") });
  };

  /**
   * Re-reads the current window. When the clock has moved the window's
   * bounds, the new bounds are adopted instead, which fetches on their own.
   */
  const refreshWindow = (refresh: () => void) => {
    const next = makeWindow(selection.days, undefined, resolution);
    if (sameBounds(next, selection.window)) refresh();
    else setSelection({ days: selection.days, window: next });
  };

  return {
    windowDays: selection.days,
    window: selection.window,
    isPast24Hours,
    resolution,
    selectWindow,
    refreshWindow,
  } as const;
}
